import { useState, useEffect } from 'react';
import { ktmToday } from '../../utils/calendar';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  MdCheckCircle, MdArrowBack, MdArrowForward,
  MdPerson, MdLock, MdFitnessCenter, MdGavel,
  MdVisibility, MdVisibilityOff, MdStar,
} from 'react-icons/md';
import { FaDumbbell } from 'react-icons/fa';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import DualDatePicker from '../../components/common/DualDatePicker';
import { getInviteInfo, completeInvite } from '../../services/trialService';
import { sanitizePhone, isValidPhone, PHONE_HINT } from '../../utils/inputRules';

const STEPS = [
  { label: 'Your Details', icon: MdPerson     },
  { label: 'Password',     icon: MdLock       },
  { label: 'Class',        icon: MdFitnessCenter },
  { label: 'Confirm',      icon: MdGavel      },
];

function PasswordStrength({ password }) {
  const checks = [
    { ok: password.length >= 8,    label: '8+ chars'  },
    { ok: /[A-Z]/.test(password),  label: 'Uppercase' },
    { ok: /[0-9]/.test(password),  label: 'Number'    },
  ];
  const score = checks.filter(c => c.ok).length;
  const color = score === 3 ? 'bg-green-500' : score === 2 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1 h-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className={`flex-1 rounded-full transition-colors ${i < score ? color : 'bg-gray-200'}`} />
        ))}
      </div>
      <div className="flex gap-4">
        {checks.map(c => (
          <span key={c.label} className={`text-xs ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>
            {c.ok ? '✓' : '○'} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

const inputCls = (err) =>
  `w-full px-3 py-2.5 border rounded-xl text-sm bg-white focus:outline-none transition-colors
   ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-primary'}`;

export default function TrialJoin() {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const { login }  = useAuth();
  const { setTenantSlug } = useTenant();

  const [step,        setStep]        = useState(0);
  const [info,        setInfo]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null); // { code, message }
  const [submitting,  setSubmitting]  = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const { register, control, handleSubmit, watch, trigger, setValue, formState: { errors } } = useForm({
    defaultValues: {
      fullName: '', phone: '', dateOfBirth: '', gender: '',
      password: '', confirmPassword: '',
      preferredClassId: '',
      termsAccepted: false,
    },
  });

  const password = watch('password');

  useEffect(() => {
    document.title = 'Complete Your Membership';
    getInviteInfo(token)
      .then(r => {
        const d = r.data;
        setInfo(d);
        // Pre-fill form with trial application data
        if (d.applicant) {
          const a = d.applicant;
          setValue('fullName',    a.fullName    || '');
          setValue('phone',       a.phone       || '');
          setValue('dateOfBirth', a.dateOfBirth ? a.dateOfBirth.split('T')[0] : '');
          setValue('gender',      a.gender      || '');
          if (a.preferredClassId) setValue('preferredClassId', a.preferredClassId);
        }
        setLoading(false);
      })
      .catch(e => {
        const code = e.response?.data?.code || 'ERROR';
        const msg  = e.response?.data?.message || 'This invitation link is invalid.';
        setError({ code, message: msg });
        setLoading(false);
      });
  }, [token, setValue]);

  const STEP_FIELDS = [
    ['fullName', 'phone', 'dateOfBirth'],
    ['password', 'confirmPassword'],
    [],
    ['termsAccepted'],
  ];

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step]);
    if (valid) setStep(s => s + 1);
  };
  const goBack = () => setStep(s => s - 1);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setServerError('');
    try {
      const res = await completeInvite(token, {
        password:         data.password,
        preferredClassId: data.preferredClassId || undefined,
        termsAccepted:    data.termsAccepted,
      });
      const { token: authToken, user, studentId, club } = res.data;

      // Store auth state
      localStorage.setItem('trainaxis_token', authToken);
      if (club?.slug) {
        localStorage.setItem('trainaxis_club_slug', club.slug);
        await setTenantSlug(club.slug);
      }
      login(user, authToken);
      setSuccessData({ studentId, clubName: club?.name || info?.club?.name, userName: user?.name });
    } catch (e) {
      const code = e.response?.data?.code;
      if (code === 'EXPIRED') {
        setError({ code, message: 'Your invitation link has expired. Please ask the club admin to resend it.' });
      } else if (code === 'ALREADY_USED') {
        setError({ code, message: 'This invitation has already been used. Try logging in.' });
      } else {
        setServerError(e.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToDashboard = () => navigate('/student/dashboard', { replace: true });

  const brandColor = info?.club?.primaryColor || '#E24B4A';

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#E24B4A', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // ── Error states ─────────────────────────────────────────────────────────────
  if (error) {
    const isExpired = error.code === 'EXPIRED';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="text-5xl mb-4">{isExpired ? '⏰' : '🔗'}</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {isExpired ? 'Invitation Expired' : 'Invalid Invitation'}
          </h2>
          <p className="text-gray-500 text-sm">{error.message}</p>
          {isExpired && (
            <p className="text-xs text-gray-400 mt-3">Contact your club admin to get a new invitation link.</p>
          )}
        </div>
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────────
  if (successData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: `linear-gradient(135deg, ${brandColor}15, #f9fafb)` }}>
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: `${brandColor}20` }}>
            <MdStar className="text-4xl" style={{ color: brandColor }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">
            Welcome, {successData.userName}!
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            You are now a full member of <strong>{successData.clubName}</strong>.
          </p>

          {successData.studentId && (
            <div className="rounded-2xl p-4 mb-6" style={{ backgroundColor: `${brandColor}10` }}>
              <p className="text-xs font-medium mb-1" style={{ color: brandColor }}>Your Student ID</p>
              <p className="text-2xl font-mono font-bold tracking-widest text-gray-800">
                {successData.studentId}
              </p>
              <p className="text-xs text-gray-400 mt-1">Keep this — you'll use it to identify yourself at the club</p>
            </div>
          )}

          <div className="text-left rounded-2xl p-4 space-y-2 mb-6" style={{ backgroundColor: '#f9fafb' }}>
            <p className="text-xs font-semibold text-gray-600 mb-2">Your account is ready:</p>
            {[
              'Personal dashboard & class schedule',
              'Attendance tracking',
              'Belt/grade progress',
              'Leaderboard & badges',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <MdCheckCircle className="text-green-500 flex-shrink-0 text-sm" />
                <span className="text-xs text-gray-600">{item}</span>
              </div>
            ))}
          </div>

          <button
            onClick={goToDashboard}
            className="w-full py-3 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: brandColor }}
          >
            Go to My Dashboard →
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────────
  const club     = info?.club;
  const classes  = info?.classes || [];
  const expiresAt = info?.expiresAt;
  const daysLeft  = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          {club?.logo ? (
            <img src={club.logo} alt={club.name} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: brandColor }}>
              <FaDumbbell className="text-white text-sm" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-bold text-gray-800 text-sm leading-tight truncate">{club?.name}</p>
            <p className="text-[11px] text-gray-400">Complete your membership registration</p>
          </div>
          {daysLeft !== null && (
            <span className={`text-[10px] font-semibold px-2 py-1 rounded-lg flex-shrink-0
              ${daysLeft <= 1 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
              {daysLeft}d left
            </span>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Invite banner */}
        <div className="rounded-2xl p-4 mb-5 text-sm" style={{ backgroundColor: `${brandColor}12` }}>
          <p className="font-semibold" style={{ color: brandColor }}>
            🎉 {club?.name} has invited you to join as a full member!
          </p>
          <p className="text-gray-500 text-xs mt-0.5">
            Complete your registration below. Your trial details have been pre-filled.
          </p>
        </div>

        {/* Step indicators */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => {
              const Icon   = s.icon;
              const done   = i < step;
              const active = i === step;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all text-sm
                      ${!done && !active ? 'bg-gray-100 text-gray-400' : 'text-white'}`}
                    style={done || active ? { backgroundColor: brandColor } : {}}
                  >
                    {done ? <MdCheckCircle className="text-lg" /> : <Icon className="text-base" />}
                  </div>
                  <span
                    className={`text-[10px] mt-1 font-medium ${!active && !done ? 'text-gray-300' : done ? 'text-gray-500' : ''}`}
                    style={active ? { color: brandColor } : {}}
                  >{s.label}</span>
                </div>
              );
            })}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(step / (STEPS.length - 1)) * 100}%`, backgroundColor: brandColor }} />
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Step 0: Verify Details ──────────────────────────────────────── */}
          {step === 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">Verify Your Details</h2>
                <p className="text-xs text-gray-400 mt-0.5">These were pre-filled from your trial application. Edit if needed.</p>
              </div>
              <Field label="Full Name" required error={errors.fullName?.message}>
                <input {...register('fullName', { required: 'Full name is required' })}
                  className={inputCls(errors.fullName)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" required error={errors.phone?.message}>
                  <input {...register('phone', { required: 'Phone is required', validate: (v) => isValidPhone(v) || PHONE_HINT })}
                    onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })}
                    className={inputCls(errors.phone)} />
                </Field>
                <Field label="Gender">
                  <select {...register('gender')} className={inputCls(false)}>
                    <option value="">Select…</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
              </div>
              <Controller name="dateOfBirth" control={control}
                rules={{ required: 'Date of birth is required' }}
                render={({ field }) => (
                  <DualDatePicker label="Date of Birth" required
                    value={field.value || ''} onChange={field.onChange}
                    max={ktmToday()}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
              {/* Email display only */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Email <span className="text-gray-400 font-normal">(from invitation)</span>
                </label>
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-500">
                  {info?.applicant?.email}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 1: Set Password ────────────────────────────────────────── */}
          {step === 1 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">Create Your Password</h2>
                <p className="text-xs text-gray-400 mt-0.5">You'll use this password to log in to your student dashboard.</p>
              </div>
              <Field label="Password" required error={errors.password?.message}>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'At least 8 characters' },
                    })}
                    placeholder="At least 8 characters"
                    className={`${inputCls(errors.password)} pr-10`}
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>
                {password && <PasswordStrength password={password} />}
              </Field>
              <Field label="Confirm Password" required error={errors.confirmPassword?.message}>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: v => v === password || 'Passwords do not match',
                    })}
                    placeholder="Re-enter your password"
                    className={`${inputCls(errors.confirmPassword)} pr-10`}
                  />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirm ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>
              </Field>
            </div>
          )}

          {/* ── Step 2: Class Preference ────────────────────────────────────── */}
          {step === 2 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">Class Preference</h2>
                <p className="text-xs text-gray-400 mt-0.5">Confirm or change the class you'd like to enroll in.</p>
              </div>
              {classes.length > 0 ? (
                <Field label="Preferred Class">
                  <select {...register('preferredClassId')} className={inputCls(false)}>
                    <option value="">No preference — I'll decide later</option>
                    {classes.map(c => (
                      <option key={c._id} value={c._id}>
                        {c.name} — {c.days?.join(', ')} {c.startTime}–{c.endTime}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1.5">You can change this later from your profile.</p>
                </Field>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <MdFitnessCenter className="text-3xl mx-auto mb-2 text-gray-200" />
                  No classes listed yet. You'll be enrolled manually by your trainer.
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Terms & Submit ──────────────────────────────────────── */}
          {step === 3 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">Accept Terms & Complete</h2>
                <p className="text-xs text-gray-400 mt-0.5">Review and accept to create your account.</p>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-xs">
                <p className="font-semibold text-gray-700 mb-2">Registration Summary</p>
                <div className="flex justify-between"><span className="text-gray-500">Club</span><span className="font-medium text-gray-800">{club?.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium text-gray-800">{watch('fullName')}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-800">{info?.applicant?.email}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Role</span><span className="font-medium text-gray-800">Student</span></div>
              </div>

              {/* Terms */}
              <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500 leading-relaxed">
                <p className="font-semibold text-gray-700 mb-1.5">Terms & Conditions</p>
                <p>By creating this account you agree to abide by {club?.name}'s code of conduct, pay fees on time, and acknowledge that participation in physical activities carries inherent risks. The club and its staff are not liable for injuries resulting from normal training activities.</p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 flex-shrink-0"
                  style={{ accentColor: brandColor }}
                  {...register('termsAccepted', { required: 'You must accept the terms' })}
                />
                <span className="text-sm text-gray-700">
                  I have read and accept the Terms & Conditions above. <span className="text-red-400">*</span>
                </span>
              </label>
              {errors.termsAccepted && <p className="text-xs text-red-500">{errors.termsAccepted.message}</p>}

              {serverError && (
                <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3">{serverError}</div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-5">
            {step > 0 ? (
              <button type="button" onClick={goBack}
                className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                <MdArrowBack /> Back
              </button>
            ) : <div />}

            {step < STEPS.length - 1 ? (
              <button type="button" onClick={goNext}
                className="flex items-center gap-2 px-6 py-2.5 text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
                style={{ backgroundColor: brandColor }}>
                Next <MdArrowForward />
              </button>
            ) : (
              <button type="submit" disabled={submitting}
                className="flex items-center gap-2 px-6 py-2.5 text-white rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity shadow-sm"
                style={{ backgroundColor: brandColor }}>
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating account…
                  </>
                ) : 'Complete Registration'}
              </button>
            )}
          </div>
        </form>

        <p className="text-center text-xs text-gray-300 mt-6">
          Powered by TrainAxis · Secure registration
        </p>
      </div>
    </div>
  );
}
